import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Float, Icon, Popover as PopoverBase, Row } from "uikit";
import { useTranslation } from "react-i18next";
import { assign, clone } from "lodash";

import Language from "../../../../../../../services/Language";
import TaxiService from "../../../../../../../services/TaxiService";
import { ExecutorStatus } from "../../../../../../../services/Executor";
import { ParkingFilter } from "../../../../../../../redux/reducers/OrdersPage/Filters";
import { defaultParkingExecutorFilters } from "../../../../../../../redux/reducers/Orders/Parking/filter";
import useObjectEditor from "../../../../../../../hooks/useObjectEditor";
import useCompanyAndTaxiServiceIdsFilter from "../../../../../../../hooks/useCompanyAndTaxiServiceIdsFilter";

import { Content, Header, Root } from "./components";

function additionalFiltersOffsetCallback({
	tracker,
	float,
}: Float.OffsetCallbackOptions) {
	if (!float || !tracker) return { x: 0, y: 0 };

	const trackerSize = tracker.getSize();
	const floatSize = { width: 1000, height: 242 }; // float.getSize();

	return {
		x: -Number(floatSize.width) + Number(trackerSize.width) + 13,
		y: Number(trackerSize.height) + 13,
	};
}

const Popover: React.FC<Popover.Props> = ({
	filter,
	taxiServiceData,
	taxiServiceIdsPosition,
	language,
	setFilter,
	open,
	onClose,
	setShowModalPropsForExecutorGroup,
}) => {
	const { t } = useTranslation();

	const [companyAndTaxiServiceError, setCompanyAndTaxiServiceError] =
		useState({
			taxiService: false,
			company: false,
		});

	const clearErrors = useCallback(() => {
		setCompanyAndTaxiServiceError({ company: false, taxiService: false });
	}, []);

	const [requestData, setRequestData] = useState<ParkingFilter>({
		companyIds: [],
		taxiServiceIds: [],
		taxiServiceIdsPosition: [],
		executorGroupIds: [],
		...defaultParkingExecutorFilters,
	});

	const [executorsStatuses, setExecutorsStatuses] = useState<
		Content.ExecutorStatus[]
	>(filter.statuses as Content.ExecutorStatus[]);

	const [companyAndTaxiServiceIds, setCompanyAndTaxiServiceIds] =
		useState<Popover.Filter>({
			companyIds: filter.companyIds,
			taxiServiceIds: filter.taxiServiceIds,
		});

	const [taxiServiceIdsTablePosition, setTaxiServiceIdsTablePosition] =
		useState<number[]>(taxiServiceIdsPosition);
	const [executorGroupIds, setExecutorGroupIds] = useState<number[]>(
		filter.executorGroupIds ?? [],
	);

	const [isYourOrderStatus, setIsYourOrderStatus] = useState(
		filter.isYourOrderStatus,
	);
	const [onlineStatus, setOnlineStatus] = useState(filter.online);
	const [workingStatus, setWorkingStatus] = useState(filter.workingStatus);

	const valueEditor = useObjectEditor(
		companyAndTaxiServiceIds,
		setCompanyAndTaxiServiceIds,
	);
	const companyAndTaxiServiceValue = valueEditor.usePicker([
		"companyIds",
		"taxiServiceIds",
	]);

	const currentValueRef = useRef(companyAndTaxiServiceValue);

	currentValueRef.current = companyAndTaxiServiceValue;

	const accessOnChange = useCallback(
		(accessValue: Popover.Filter) => {
			const assignValue: Partial<Popover.Filter> = {
				...accessValue,
			};

			valueEditor.assign(assignValue);
		},
		[valueEditor],
	);

	const setField = useCallback(
		<Field extends keyof Popover.Filter>(
			field: Field,
			fieldValue: Popover.Filter[Field],
		) => {
			currentValueRef.current = assign(clone(currentValueRef.current), {
				[field]: fieldValue,
			});

			accessOnChange(currentValueRef.current);
		},
		[accessOnChange],
	);

	const onChangeTaxiServiceIds = useCallback(
		(taxiServiceIds: number[] | ["all"]) => {
			// setTaxiServiceIdsError(false);

			setField("taxiServiceIds", taxiServiceIds);
		},
		[setField],
	);

	const onChangeCompanyIds = useCallback(
		(companyIds: number[] | ["all"]) => {
			// setCompanyIdsError(false);

			setField("companyIds", companyIds);
		},
		[setField],
	);

	const [
		possibleCompanies,
		possibleTaxiServices,
		companyIds,
		taxiServiceIds,
		setCompanyIds,
		setTaxiServiceIds,
	] = useCompanyAndTaxiServiceIdsFilter(
		companyAndTaxiServiceValue.companyIds,
		companyAndTaxiServiceValue.taxiServiceIds,
		onChangeCompanyIds,
		onChangeTaxiServiceIds,
	);

	const clearFilters = useCallback(() => {
		setCompanyAndTaxiServiceIds({
			companyIds: ["all"],
			taxiServiceIds: ["all"],
		});
		setExecutorGroupIds([]);
		setExecutorsStatuses([
			...defaultParkingExecutorFilters.statuses,
		] as ExecutorStatus[]);
		setOnlineStatus([...defaultParkingExecutorFilters.online]);
		setWorkingStatus([...defaultParkingExecutorFilters.workingStatus]);
		setIsYourOrderStatus(defaultParkingExecutorFilters.isYourOrderStatus);
		setTaxiServiceIdsTablePosition(taxiServiceIdsPosition);
		clearErrors();
	}, [clearErrors, taxiServiceIdsPosition]);

	const didOpenRef = useRef(false);

	const apply = useCallback(() => {
		const nextCompanyIds = companyIds.length
			? companyIds
			: requestData.companyIds;
		const nextTaxiServiceIds = taxiServiceIds.length
			? taxiServiceIds
			: requestData.taxiServiceIds;

		if (!nextTaxiServiceIds.length)
			companyAndTaxiServiceError.taxiService = true;
		if (!nextCompanyIds.length) companyAndTaxiServiceError.company = true;
		if (!nextTaxiServiceIds.length || !nextCompanyIds.length) {
			setCompanyAndTaxiServiceError(() => ({
				...companyAndTaxiServiceError,
			}));

			return;
		}

		setFilter({
			...requestData,
			companyIds: nextCompanyIds,
			taxiServiceIds: nextTaxiServiceIds,
			executorGroupIds,
			taxiServiceIdsPosition: taxiServiceIdsTablePosition.filter((id) =>
				nextTaxiServiceIds.some(
					(taxiServiceId) => Number(taxiServiceId) === Number(id),
				),
			),
			statuses: executorsStatuses,
			online: onlineStatus,
			workingStatus,
			isYourOrderStatus,
			query: filter.query,
		});
		onClose();
		clearErrors();
	}, [
		clearErrors,
		companyAndTaxiServiceError,
		companyIds,
		executorGroupIds,
		executorsStatuses,
		filter.query,
		isYourOrderStatus,
		onClose,
		onlineStatus,
		requestData,
		setFilter,
		taxiServiceIds,
		taxiServiceIdsTablePosition,
		workingStatus,
	]);

	const updateFilters = useCallback(() => {
		setCompanyAndTaxiServiceIds({
			companyIds: filter.companyIds,
			taxiServiceIds: filter.taxiServiceIds,
		});
		setExecutorGroupIds(filter.executorGroupIds ?? []);
		setExecutorsStatuses((filter.statuses ?? []) as ExecutorStatus[]);
		setOnlineStatus(filter.online ?? []);
		setWorkingStatus(filter.workingStatus ?? []);
		setIsYourOrderStatus(filter.isYourOrderStatus);
		setTaxiServiceIdsTablePosition(taxiServiceIdsPosition);
		clearErrors();
	}, [
		clearErrors,
		filter.companyIds,
		filter.executorGroupIds,
		filter.isYourOrderStatus,
		filter.online,
		filter.statuses,
		filter.taxiServiceIds,
		filter.workingStatus,
		taxiServiceIdsPosition,
	]);

	const cancel = useCallback(() => {
		updateFilters();
		onClose();
		clearErrors();
	}, [clearErrors, onClose, updateFilters]);

	useEffect(() => {
		if (open && !didOpenRef.current) {
			updateFilters();
		}

		didOpenRef.current = open;
	}, [open, updateFilters]);

	return (
		<PopoverBase
			open={open}
			containerId="root"
			trackerId="filters"
			offset={additionalFiltersOffsetCallback}
			onClose={cancel}
			style={{ boxShadow: "rgba(0,0,0,0.2) 0px 6px 24px 0px" }}
		>
			<Root align="stretch" gaps="16px 20px">
				<Header onClose={cancel} />

				<Content
					taxiServiceData={taxiServiceData}
					taxiServiceIdsPosition={taxiServiceIdsPosition}
					language={language}
					onChangeRequestData={setRequestData}
					possibleCompanies={possibleCompanies}
					possibleTaxiServices={possibleTaxiServices}
					companyIds={companyIds}
					taxiServiceIds={taxiServiceIds}
					setCompanyIds={setCompanyIds}
					setTaxiServiceIds={setTaxiServiceIds}
					taxiServiceIdsTablePosition={taxiServiceIdsTablePosition}
					setTaxiServiceIdsTablePosition={
						setTaxiServiceIdsTablePosition
					}
					executorsStatuses={executorsStatuses}
					setExecutorsStatuses={setExecutorsStatuses}
					error={companyAndTaxiServiceError}
					onChangeError={setCompanyAndTaxiServiceError}
					onlineStatus={onlineStatus}
					setOnlineStatus={setOnlineStatus}
					workingStatus={workingStatus}
					setWorkingStatus={setWorkingStatus}
					isYourOrderStatus={isYourOrderStatus}
					setIsYourOrderStatus={setIsYourOrderStatus}
					executorGroupIds={executorGroupIds}
					setExecutorGroupIds={setExecutorGroupIds}
					setShowModalPropsForExecutorGroup={
						setShowModalPropsForExecutorGroup
					}
				/>

				<Row justify="space-between">
					<Button.Button
						icon={<Icon size={18} id="refresh" />}
						variant="secondary"
						onClick={clearFilters}
					/>
					<Row gaps="20px">
						<Button.Button
							variant="secondary"
							text={t("cancel") || "Cancel"}
							onClick={cancel}
						/>
						<Button.Button
							text={
								t(
									"orderPageWidgets.parking.filter.popover.str0",
								) ?? ""
							}
							onClick={apply}
						/>
					</Row>
				</Row>
			</Root>
		</PopoverBase>
	);
};

declare namespace Popover {
	interface Filter {
		companyIds: number[] | ["all"];
		taxiServiceIds: number[] | ["all"];
	}

	interface Props {
		taxiServiceData: TaxiService.Model[] | undefined;
		taxiServiceIdsPosition: number[];
		language: Language;
		filter: ParkingFilter;
		setFilter: (parkingFilter: ParkingFilter) => void;
		open: boolean;
		onClose: () => void;
		setShowModalPropsForExecutorGroup: (value: boolean) => void;
	}
}

export default Popover;
